/*
 * Copyright (c) 2026. World Wide Generation Ltd
 */

import { Badge } from 'reactstrap';
import type { UniversalTrackerPlain } from '@g17eco/types/universalTracker';
import { ProvenanceModal } from '@features/utr-modal/ProvenanceModal';
import UniversalTrackerModel from '../../../../../../model/UniversalTracker';
import { computeDiffPercentage, formatValue } from './utils';

const MAX_VALUE_LENGTH = 80;

interface TruncatedValueProps {
  value?: string | number | string[];
  unit?: string;
  resolveLabel?: (code: string) => string;
}

export const TruncatedValue = ({ value, unit, resolveLabel }: TruncatedValueProps) => {
  if (Array.isArray(value)) {
    return (
      <span className='d-inline-flex flex-wrap gap-1'>
        {value.map((v) => (
          <Badge key={v} color='secondary' pill className='fw-normal'>{resolveLabel ? resolveLabel(v) : v}</Badge>
        ))}
      </span>
    );
  }
  const full = formatValue(value, unit);
  if (full.length <= MAX_VALUE_LENGTH) {
    return <>{full}</>;
  }
  return <span title={full}>{full.slice(0, MAX_VALUE_LENGTH)}…</span>;
};

export const DiffCell = ({ hasEntries, entries, hasDiscrepancy, surveyVal, intVal }: {
  hasEntries: boolean;
  entries?: Array<{ hasDiscrepancy: boolean }>;
  hasDiscrepancy?: boolean;
  surveyVal?: string | number | string[];
  intVal?: string | number | string[];
}) => {
  if (hasEntries && entries) {
    return <EntryDifferCount entries={entries} />;
  }
  if (hasDiscrepancy) {
    return <DiffBadge survey={surveyVal} proposed={intVal} hasDiscrepancy />;
  }
  return <span className='text-ThemeTextLight'>—</span>;
};

export const EntryDifferCount = ({ entries }: { entries: Array<{ hasDiscrepancy: boolean }> }) => {
  const differCount = entries.filter((e) => e.hasDiscrepancy).length;
  if (differCount > 0) {
    return <Badge color='danger' pill>{differCount} differ</Badge>;
  }
  return <Badge color='success' pill>All match</Badge>;
};

interface DiffBadgeProps {
  survey?: string | number | string[];
  proposed?: string | number | string[];
  hasDiscrepancy?: boolean;
}

export const DiffBadge = ({ survey, proposed, hasDiscrepancy }: DiffBadgeProps) => {
  const diff = computeDiffPercentage(survey, proposed);
  if (diff.pct === undefined) {
    if (hasDiscrepancy) {
      return <span className='badge rounded-pill fw-bold background-ThemeDangerExtralight text-ThemeDangerMedium'>Differs</span>;
    }
    return <span className='text-ThemeTextLight'>—</span>;
  }
  const sign = diff.pct >= 0 ? '+' : '';
  const bgClass = diff.category === 'increase'
    ? 'background-ThemeDangerExtralight text-ThemeDangerMedium'
    : 'background-ThemeSuccessExtralight text-ThemeSuccessMedium';
  return <span className={`badge rounded-pill fw-bold ${bgClass}`}>{sign}{diff.pct.toFixed(1)}%</span>;
};

interface ProvenanceModalForMetricProps {
  provenanceUtrvId: { utrvId: string; utrCode: string } | null;
  utrByCode: Map<string, UniversalTrackerPlain>;
  onClose: () => void;
}

export const ProvenanceModalForMetric = ({ provenanceUtrvId, utrByCode, onClose }: ProvenanceModalForMetricProps) => {
  const utr = provenanceUtrvId ? utrByCode.get(provenanceUtrvId.utrCode) : undefined;
  if (!provenanceUtrvId || !utr) {
    return null;
  }
  return (
    <ProvenanceModal
      isOpen={true}
      toggle={onClose}
      utr={new UniversalTrackerModel(utr)}
      utrValueId={provenanceUtrvId.utrvId}
    />
  );
};
